import { AxiosPromise } from 'axios';
import { apiUrl, apiService } from '../apiService';
import PageableType from '../utils/pageableType';
import {
  CreateProviderLimitDto,
  ManualRefundDto,
  ManualRefundFilterDto,
  ManualRefundUpdateDto,
  NewCashTransactionDto,
  OrganizationDto,
  PaymentConfigDto,
  PaymentInfoDto,
  ProviderInfoDto,
  ProviderLimitDto,
  TransactionBillingDataDto,
  TransactionDto,
  TransactionErrorInfoDto,
  TransactionInfoDto,
  TransactionsFilterDto,
  UpdateProviderLimitDto,
} from './paymentService.dto';

class PaymentService {
  static BASE_URL = `${apiUrl}/api/payment`;

  fetchManualRefunds(filters: ManualRefundFilterDto): AxiosPromise<PageableType<ManualRefundDto>> {
    const url = `${PaymentService.BASE_URL}/admin/manual-refunds`;
    return apiService.get(url, { ...filters });
  }

  updateManualRefund(manualRefundId: number, data: ManualRefundUpdateDto) {
    const url = `${PaymentService.BASE_URL}/admin/manual-refunds/${manualRefundId}`;
    return apiService.patch(url, {}, {}, data);
  }

  createCashTransaction(data: NewCashTransactionDto): AxiosPromise<TransactionDto> {
    const url = `${PaymentService.BASE_URL}/admin/cash-transactions`;
    return apiService.post(url, {}, {}, data);
  }

  fetchTransactions(filters: TransactionsFilterDto): AxiosPromise<PageableType<TransactionInfoDto>> {
    const url = `${PaymentService.BASE_URL}/admin/transactions`;
    return apiService.get(url, { ...filters });
  }

  fetchTransactionErrors(transactionId: number): AxiosPromise<TransactionErrorInfoDto[]> {
    const url = `${PaymentService.BASE_URL}/admin/transactions/${transactionId}/errors`;
    return apiService.get(url);
  }

  fetchPayments(): AxiosPromise<PaymentInfoDto[]> {
    const url = `${PaymentService.BASE_URL}/admin/payments`;
    return apiService.get(url);
  }

  updatePayment(paymentId: number, enabled: boolean): AxiosPromise<PaymentInfoDto> {
    const url = `${PaymentService.BASE_URL}/admin/payments/${paymentId}`;
    return apiService.patch(url, {}, {}, { enabled });
  }

  fetchClientConfiguration(currencyCode: string): AxiosPromise<PaymentConfigDto> {
    const url = `${PaymentService.BASE_URL}/public/client-configuration`;
    return apiService.get(url, { currencyCode }, {});
  }

  fetchTransactionBillingData(transactionId: number): AxiosPromise<TransactionBillingDataDto> {
    const url = `${PaymentService.BASE_URL}/admin/transactions/${transactionId}/billing-data`;
    return apiService.get(url);
  }

  fetchProviders(): AxiosPromise<ProviderInfoDto[]> {
    const url = `${PaymentService.BASE_URL}/admin/payments/payment-providers`;
    return apiService.get(url);
  }

  fetchProviderLimits(): AxiosPromise<ProviderLimitDto[]> {
    const url = `${PaymentService.BASE_URL}/admin/provider-limits`;
    return apiService.get(url);
  }

  addProviderLimit(request: CreateProviderLimitDto): AxiosPromise<ProviderLimitDto> {
    const url = `${PaymentService.BASE_URL}/admin/provider-limits`;
    return apiService.post(url, {}, {}, request);
  }

  updateProviderLimit(id: number, request: UpdateProviderLimitDto): AxiosPromise<ProviderLimitDto> {
    const url = `${PaymentService.BASE_URL}/admin/provider-limits/${id}`;
    return apiService.put(url, {}, {}, request);
  }

  deleteProviderLimit(id: number) {
    const url = `${PaymentService.BASE_URL}/admin/provider-limits/${id}`;
    return apiService.delete(url);
  }

  add(file: File, gzip: boolean = false) {
    const url = `${PaymentService.BASE_URL}/admin/organizations`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('gzip', gzip.toString());

    return apiService.post(url, {}, { 'Content-Type': 'multipart/form-data' }, formData);
  }

  refresh(file: File, gzip: boolean = false) {
    const url = `${PaymentService.BASE_URL}/admin/organizations`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('gzip', gzip.toString());

    return apiService.put(url, {}, { 'Content-Type': 'multipart/form-data' }, formData);
  }

  deleteBulk(file: File, gzip: boolean = false) {
    const url = `${PaymentService.BASE_URL}/admin/organizations`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('gzip', gzip.toString());

    return apiService.delete(url, {}, { 'Content-Type': 'multipart/form-data' }, formData);
  }

  deleteSingle(ein: string) {
    const url = `${PaymentService.BASE_URL}/admin/organizations`;
    return apiService.delete(`${url}/${ein}`);
  }

  fetchOrganizations(params: any): AxiosPromise<PageableType<OrganizationDto>> {
    const url = `${PaymentService.BASE_URL}/admin/organizations`;
    return apiService.get(url, params);
  }
}

const paymentService = new PaymentService();

export { paymentService, PaymentService };
